/**
 * English Products Translations
 * 产品页翻译模块 - 英语
 *
 * 包含产品列表、产品详情、五个产品完整翻译
 */

import type { ProductsTranslations } from '../../types';

export const enProducts: ProductsTranslations = {
  title: 'All Products',
  subtitle: 'Discover the perfect heating solution for your space',
  viewDetails: 'View Details',
  header: {
    title: 'All Products',
    subtitle: 'Discover the perfect heating solution for your home, office, or industrial space.',
  },
  filters: {
    all: 'All',
    searchPlaceholder: 'Search products...',
  },
  catalog: 'Download Full Catalog',
  discoverMore: 'Discover More',
  viewSpecs: 'View Specifications',
  categories: {
    infraredHeater: 'Infrared Heater',
    portableHeater: 'Portable Heater',
    gasHeater: 'Gas Patio Heaters',
  },
  navDesc: {
    infrared: 'Wall and ceiling radiant panels',
    portable: 'Freestanding, tabletop and hanging',
    gas: 'Patio heaters and fire tables',
  },
  viewAllProducts: 'View all products',
  gasSubtitle: 'Outdoor gas heaters and fire tables for patios and hospitality spaces.',
  categoryLanding: {
    gas: {
      title: 'Gas Patio Heaters & Fire Tables',
      subtitle:
        'Open-air heating from 8 kW to 50,000 BTU, with 304 stainless-steel burners, glass wind guards and LPG configurations matched to your destination market.',
      empty: 'No products found in this category.',
    },
    infrared: {
      title: 'Infrared Heaters',
      subtitle: 'Energy-efficient far infrared radiant heaters for patios, restaurants, workshops, and living spaces. Wall-mounted and ceiling options from 1000W to 4000W.',
      empty: 'No products found in this category.',
    },
    portable: {
      title: 'Portable Heaters',
      subtitle: 'Flexible heating solutions for home and workplace—easy to move, quick to warm, built for everyday comfort.',
      empty: 'No products found in this category.',
    },
  },
  detail: {
    technicalSpecs: 'Technical Specifications',
    relatedProducts: 'Related {{category}} Products',
    requestQuote: 'Request Quote',
    backToProducts: 'Back to Products',
    productNotFound: 'Product Not Found',
    specsAvailableOnRequest: 'Specifications available upon request.',
    noMatchingProducts: 'No matching products',
    noProducts: 'No product data',
  },
  items: {
    p1: {
      name: '13A Series',
      description: 'Energy-efficient outdoor far infrared heater with arched aerospace heating elements. 1000W-4000W models for patios, restaurants, and workshops.',
      longDescription:
        'The 13A Series is a reliable far infrared heater designed for outdoor and semi-outdoor spaces. Unlike traditional convection heaters that waste energy warming air, this infrared radiant heater converts up to 99% of electricity into direct warmth — reducing power consumption by up to 80%. The durable aerospace heating elements with arched plate deliver a large radiant surface for even heat distribution. IPX4 waterproof and silent operation make it ideal for restaurant patios, outdoor decks, sunrooms, and workshops. Available in 1000W to 4000W to cover areas from 10 m² to 50 m².',
      category: 'Infrared Heater',
      features: ['Eco-Friendly', 'Safe & Quiet', 'Long Lifespan', '350°C High Temp'],
      specs: [
        { label: 'Model', value: 'NR10/18/24/32/40-13A' },
        { label: 'Power', value: '2400W' },
        { label: 'Cover Area', value: '30 m²' },
        { label: 'Voltage/Frequency', value: '230V, 50/60Hz' },
        { label: 'Rated Current', value: '10.4A' },
        { label: 'Surface Temperature', value: '300°C' },
        { label: 'Dimension', value: '1500×189×67mm' },
        { label: 'N/G Weight', value: '8.8/10.6 kg' },
      ],
    },
    p2: {
      name: '13B Series',
      description: 'Remote-control far infrared heater with thermostat and LED indicator. Energy-efficient wall-mounted heating for living rooms, bedrooms, and offices.',
      longDescription:
        'The 13B Series is a classic wall-mounted infrared heater with excellent energy-saving performance. Equipped with a built-in thermostat and remote control, it maintains your desired temperature automatically — no wasted energy. The red LED indicator shows heating status at a glance. Durable aerospace heating elements with arched plate provide 99% electric-to-heat conversion efficiency. Silent, dust-free radiant warmth makes it perfect for living rooms, bedrooms, and offices where quiet comfort matters.',
      category: 'Infrared Heater',
      features: ['Remote Control', 'LED Indicator', 'Eco-Friendly', 'High Efficiency', '300°C Surface'],
      specs: [
        { label: 'Model', value: 'NR10/18/24/32-13B' },
        { label: 'Power', value: '2400W' },
        { label: 'Cover Area', value: '30 m²' },
        { label: 'Voltage/Frequency', value: '230V, 50/60Hz' },
        { label: 'Rated Current', value: '10.4A' },
        { label: 'Surface Temperature', value: '300°C' },
        { label: 'Dimension', value: '1500×189×67mm' },
        { label: 'N/G Weight', value: '8.8/10.6 kg' },
      ],
    },
    p3: {
      name: '13C Series',
      description: 'Far infrared patio heater with built-in Bluetooth speaker. 4 power levels, nano-coated panel. Perfect for outdoor restaurants and entertainment areas.',
      longDescription:
        'The 13C Series combines far infrared heating with entertainment — a heater and Bluetooth speaker in one. Stream music from your phone via the built-in 3D stereo speaker while enjoying silent, radiant warmth. The nano-coated heating panel delivers high infrared emissivity for efficient energy conversion. Four power settings (25%/50%/75%/100%) let you adjust heat output for any occasion. Ideal for outdoor restaurant patios, entertainment areas, and semi-outdoor comfort spaces where ambiance matters as much as warmth.',
      category: 'Infrared Heater',
      features: ['Bluetooth Speaker', '4 Power Levels', 'Far Infrared Comfort', 'Bluetooth Audio', 'Silent Operation'],
      specs: [
        { label: 'Model', value: 'NR10/18/24/32-13C' },
        { label: 'Power', value: '1800W' },
        { label: 'Cover Area', value: '20 m²' },
        { label: 'Voltage/Frequency', value: '230V, 50/60Hz' },
        { label: 'Rated Current', value: '7.8A' },
        { label: 'Surface Temperature', value: '300°C' },
        { label: 'Dimension', value: '1370×189×67mm' },
        { label: 'N/G Weight', value: '8.3/10.2 kg' },
        { label: 'Container Load', value: '705/1632; 480/1156; 355/884; 270/612' },
      ],
    },
    p4: {
      name: '22B Series',
      description: 'Premium glass ceramic infrared panel heater with WiFi Tuya app control. 1600W-3200W radiant heating for modern living rooms and offices.',
      longDescription:
        'The 22B Series is a premium infrared panel heater featuring glass ceramic heating surfaces that withstand up to 950°C. The special glass reflection creates a sleek, modern look for any room. Control heating remotely via the WiFi Tuya app or the included remote — set schedules, adjust temperature, and save energy from your phone. Provides clean radiant warmth with no noise, no fans, and no dried air. The far infrared wavelength also supports health benefits by promoting metabolism. Available in 1600W, 2200W, and 3200W models covering 20-40 m².',
      category: 'Infrared Heater',
      features: ['WiFi Tuya App', 'Remote Control', 'Glass Ceramic Panel', 'Fast Heating', 'Health Care'],
      specs: [
        { label: 'Model', value: 'NR16/22/32-22B' },
        { label: 'Power', value: '1.6/2.2/3.2 kW' },
        { label: 'Cover Area', value: '20/30/40 m²' },
        { label: 'Voltage/Frequency', value: '220-240V~50/60Hz' },
        { label: 'Rated Current', value: '7/9.6/13.9A' },
        { label: 'Surface Temperature', value: '380°C' },
        { label: 'Dimension', value: '810/1160/1660 × 180 × 77 mm' },
        { label: 'N/G Weight', value: '5/6.1; 7/8.2; 9.9/11.7 kg' },
        { label: 'Container Load', value: '728/1638; 507/1162; 377/826' },
      ]
    },
    p5: {
      name: '13W Series',
      description: 'Smart WiFi far infrared heater with IPX4 waterproof rating. App-controlled radiant heating for indoor and outdoor patios, balconies, and terraces.',
      longDescription:
        "The 13W Series combines Tuya WiFi app control, an LCD remote, an LED status display and four power levels. For indoor rooms and covered terraces or balconies with a suitable mounting wall, install using compatible brackets and the model’s specified clearances. IPX4 describes splash protection.",
      category: 'Infrared Heater',
      features: ['WiFi Tuya App', 'LED Display', 'LCD Remote', '4 Power Levels', 'IPX4 Waterproof'],
      specs: [
        { label: 'Model', value: 'NR10/18/24/32-13W' },
        { label: 'Power', value: '1.0/1.8/2.4/3.2 kW' },
        { label: 'Cover Area', value: '10/20/30/40 m²' },
        { label: 'Voltage/Frequency', value: '220-240V~50/60Hz' },
        { label: 'Rated Current', value: '4.3/7.8/10.4/13.9A' },
        { label: 'Surface Temperature', value: '300°C' },
        { label: 'Dimension', value: '755/1150/1655/2155 × 189 × 67 mm' },
        { label: 'N/G Weight', value: '4.4/5.5; 6.9/8.1; 9.5/11.3; 12.3/14.3 kg' },
        { label: 'Container Load', value: '840/1680/2100; 530/1080/1350; 570/900/980; 350/700/800' },
      ]
    },
    p6: {
      name: '14A Series',
      description: 'Classic arch-shaped far infrared heater with nano-coated panel. 1000W-4000W energy-efficient radiant heating for terraces, patios, and balconies.',
      longDescription:
        "The 14A Series uses an arch-shaped nano-coated panel with power options from 1000W to 4000W. It has no built-in controller. Choose a suitable mounting wall in an indoor room or covered area, and confirm the brackets, clearances and control arrangement for the selected model. IPX4 describes splash protection.",
      category: 'Infrared Heater',
      features: ['Arch Design', 'Multiple Power', 'IPX4 Waterproof', 'Fast Heating', 'Energy Efficient'],
      specs: [
        { label: 'Model', value: '' },
        { label: 'Power', value: '' },
        { label: 'Cover Area', value: '' },
        { label: 'Voltage/Frequency', value: '' },
        { label: 'Rated Current', value: '' },
        { label: 'Surface Temperature', value: '' },
        { label: 'Dimension', value: '' },
        { label: 'N/G Weight', value: '' },
        { label: 'Container Load', value: '' },
      ]
    },
    p7: {
      name: '14B Series',
      description: "Remote-controlled wall-mounted far infrared heater for indoor rooms and covered terraces with a suitable mounting wall.",
      longDescription:
        "The 14B Series combines an arch-shaped nano-coated panel with remote control, an LED indicator, four power levels and a 0/2/4/6/8-hour timer. Install on a suitable wall in an indoor or covered location using compatible brackets and the specified clearances. Its slim body does not establish suitability for other mounting structures.",
      category: 'Infrared Heater',
      features: ['Remote Control', 'Timer Function', 'Arch Design', 'IPX4 Waterproof', 'Fast Heating'],
      specs: [
        { label: 'Model', value: '' },
        { label: 'Power', value: '' },
        { label: 'Cover Area', value: '' },
        { label: 'Voltage/Frequency', value: '' },
        { label: 'Rated Current', value: '' },
        { label: 'Surface Temperature', value: '' },
        { label: 'Dimension', value: '' },
        { label: 'N/G Weight', value: '' },
        { label: 'Container Load', value: '' },
      ]
    },
    p8: {
      name: '23W Series',
      description: 'Slim far infrared heater with Tuya Smart WiFi app, voice control, remote control, and timer for indoor spaces and covered patios.',
      longDescription:
        "The 23W Series combines a slim black nano-coated panel with Tuya Smart WiFi app control and compatible voice commands. The included remote provides four power levels and a 0/2/4/6/8-hour timer. Install on a suitable wall in an indoor or covered area with compatible brackets and the clearances specified for the model.",
      category: 'Infrared Heater',
      features: ['Tuya Smart App', 'Voice Control', 'Remote Control', 'Timer Function', '4 Power Levels', 'Fast Heating'],
      specs: [
        { label: 'Model', value: 'NR10/18/24/32-23W' },
        { label: 'Power', value: '1.0/1.8/2.4/3.2 kW' },
        { label: 'Cover Area', value: '10/20/30/40 m²' },
        { label: 'Voltage/Frequency', value: '220-240V~50/60Hz' },
        { label: 'Rated Current', value: '4.3/7.8/10.4/13.9A' },
        { label: 'Surface Temperature', value: '300°C' },
        { label: 'Dimension', value: '693/1093/1593/2093 × 151 × 52 mm' },
        { label: 'N/G Weight', value: '3.4/4.2; 5.3/6.4; 7.6/9.5; 9.9/12.5 kg' },
        { label: 'Container Load', value: '1248; 800; 528; 412 (20HQ)' },
      ]
    },
    p20: {
      name: '23A Series',
      description: 'Slim far infrared heater with ordinary remote control, timer, and 4 power levels—no WiFi or app required.',
      longDescription:
        'The 23A Series is a slim far infrared heater operated by an ordinary handheld remote, with no WiFi or app connection. The remote provides 4 power levels (25%/50%/75%/100%) and a 0/2/4/6/8-hour timer. Its sleek black nano-coated panel delivers fast, even, silent and flameless radiant heat. Available from 1000W to 3200W for spaces from 10-40 m², with a compact 52mm profile suited to homes, restaurants, offices and covered terraces.',
      category: 'Infrared Heater',
      features: ['Remote Control', 'Timer Function', '4 Power Levels', 'Slim Design', 'Fast Heating', 'Silent Operation'],
      specs: [
        { label: 'Model', value: '' },
        { label: 'Power', value: '' },
        { label: 'Cover Area', value: '' },
        { label: 'Voltage/Frequency', value: '' },
        { label: 'Rated Current', value: '' },
        { label: 'Surface Temperature', value: '' },
        { label: 'Dimension', value: '' },
        { label: 'N/G Weight', value: '' },
        { label: 'Container Load', value: '' },
      ]
    },
    p21: {
      name: '25W Series',
      description: 'Wall-mounted far infrared heater with built-in WiFi, Tuya app control, LED level display and four power levels from 1000W to 3200W.',
      longDescription:
        'The 25W Series is a slim wall-mounted far infrared heater with built-in WiFi for Tuya Smart app control — switch the panel on, change power level and set schedules from your phone, or use the supplied wireless remote with its WiFi pairing button. The red LED display on the control housing shows the current power level. Four power levels run from 1000W to 3200W and cover 10 m² to 40 m², and every model shares the same 67 mm aluminium body. A coiled far-infrared heating element runs the length of the panel inside an extruded aluminium body, bringing the ribbed aluminium face to a 300°C surface temperature.',
      category: 'Infrared Heater',
      features: ['WiFi Tuya App', 'LED Level Display', 'Wireless Remote', '4 Power Levels', '300°C Surface', '67 mm Slim Body'],
      specs: [
        { label: 'Model', value: 'NR10/18/24/32-25W' },
        { label: 'Power', value: '1.0/1.8/2.4/3.2 kW' },
        { label: 'Cover Area', value: '10/20/30/40 m²' },
        { label: 'Voltage/Frequency', value: '220-240V~50/60Hz' },
        { label: 'Rated Current', value: '4.3/7.8/10.4/13.9A' },
        { label: 'Surface Temperature', value: '300°C' },
        { label: 'Dimension', value: '715/1115/1615/2115 × 189 × 67 mm' },
        { label: 'N/G Weight', value: '4.4/5.5; 6.9/8.1; 9.5/11.3; 12.3/14.3 kg' },
        { label: 'Container Load', value: '945/1920/2176; 615/1290/1462; 435/885/1003; 345/705/799' },
      ]
    },
    p9: {
      name: 'JHL020A',
      description: 'Compact carbon fiber heater with tip-over protection. Perfect for personal heating under desks or tables.',
      longDescription:
        'JHL020A is a compact and efficient under table heater designed for personal warming. Featuring a carbon fiber heating tube for rapid and even heat distribution. Equipped with rocker switch control and tip-over protection for safety. The cool-touch mesh design prevents accidental burns while providing efficient warmth. Lightweight construction with EU plug makes it perfect for home or office use.',
      category: 'Portable Heater',
      features: ['Tip-over Protection', 'Cool-touch Mesh', 'Compact Design', 'Rapid Heating', 'Energy Efficient'],
      specs: [
        { label: 'Model', value: 'JHL020A' },
        { label: 'Product Name', value: 'Under Table Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '1200W' },
        { label: 'Control', value: 'Rocker Switch' },
        { label: 'Heating Element', value: '1 Carbon fiber Heating tube' },
        { label: 'Plug', value: 'EU-plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Material', value: 'Iron, Aluminum' }
      ]
    },
    p10: {
      name: 'JHL023',
      description: 'Powerful carbon fiber heater with dual heat settings and foldable handle. Portable heating solution for any space.',
      longDescription:
        'JHL023 is a powerful and portable freestanding heater featuring dual carbon fiber heating tubes for efficient warmth. Two heat settings (750W + 750W) allow flexible temperature control. The rotary switch provides easy operation, while the foldable handle makes it highly portable. Made with durable plastic and steel construction, this heater is perfect for living rooms, bedrooms, offices, or any space that needs extra warmth.',
      category: 'Portable Heater',
      features: ['Dual Heat Settings', 'Foldable Handle', 'Carbon Fiber Heating', 'Portable Design', 'Easy Operation'],
      specs: [
        { label: 'Model', value: 'JHL023' },
        { label: 'Product Name', value: 'Freestanding Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '1500W (max)' },
        { label: 'Settings', value: '750W + 750W' },
        { label: 'Control', value: 'Rotary switch' },
        { label: 'Heating Element', value: 'Carbon fiber heating tube x 2' },
        { label: 'Plug', value: 'EU plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Material', value: 'Plastic, Steel' },
        { label: 'Feature', value: 'Foldable handle' }
      ]
    },
    p11: {
      name: 'JHL023-05',
      description: 'Compact carbon fiber heater with dual heat settings. Energy-efficient heating for small to medium spaces.',
      longDescription:
        'JHL023-05 is a compact and energy-efficient freestanding heater perfect for small to medium spaces. Featuring dual carbon fiber heating tubes with two adjustable settings (400W + 400W), it provides comfortable warmth while conserving energy. The rotary switch allows easy operation, and the foldable handle ensures convenient storage and portability. Ideal for bedrooms, home offices, or personal workspaces.',
      category: 'Portable Heater',
      features: ['Dual Heat Settings', 'Foldable Handle', 'Carbon Fiber Heating', 'Compact & Portable', 'Energy Efficient'],
      specs: [
        { label: 'Model', value: 'JHL023-05' },
        { label: 'Product Name', value: 'Freestanding Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '800W (max)' },
        { label: 'Settings', value: '400W + 400W' },
        { label: 'Control', value: 'Rotary switch' },
        { label: 'Heating Element', value: 'Carbon fiber heating tube x 2' },
        { label: 'Plug', value: 'EU plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Material', value: 'Plastic, Steel' },
        { label: 'Feature', value: 'Foldable handle' }
      ]
    },
    p12: {
      name: 'JHL021-S',
      description: 'Versatile heater with LED ambient light and Bluetooth speaker. Perfect for creating a cozy atmosphere.',
      longDescription:
        'JHL021-S is a innovative heater that combines heating functionality with entertainment features. Features 16-color LED ambient lighting to create the perfect atmosphere for any setting. Equipped with a Bluetooth speaker for streaming music from your mobile device. The rocker switch controls heat settings, while the remote controls Bluetooth and LED functions. Features tip-over protection for safety and CoolTouch mesh to prevent burns. The carbon fiber heating tube provides efficient and rapid heating. Ideal for living rooms, bedrooms, or any indoor space where you want warmth and ambiance.',
      category: 'Portable Heater',
      features: ['16-Color LED Light', 'Bluetooth Speaker', 'Tip-over Protection', 'CoolTouch Mesh', 'Remote Control'],
      specs: [
        { label: 'Model', value: 'JHL021-S' },
        { label: 'Product Name', value: 'LED Bluetooth Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '1500W' },
        { label: 'Control', value: 'Rocker Switch (Heat), Remote (BT & LED)' },
        { label: 'LED', value: '16-colour light' },
        { label: 'Heating Element', value: '1 Carbon fiber heating tube' },
        { label: 'Plug', value: 'EU-plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Safety', value: 'Tip-over protection, CoolTouch Mesh' },
        { label: 'Material', value: 'Iron, Aluminum' }
      ]
    },
    p13: {
      name: 'JHL004-B',
      description: 'Powerful 3000W halogen heater with telescopic pole. Adjustable height for customized heating coverage.',
      longDescription:
        'JHL004-B is a powerful freestanding patio heater designed for outdoor and semi-outdoor spaces. Features two halogen heating tubes providing efficient and comfortable warmth with three adjustable power settings (1200W-1800W-3000W). The telescopic pole allows height adjustment from 1.6m to 2.08m, enabling customized heating coverage for different areas. Equipped with both remote control and touch button for convenient operation. Features tip-over protection for safety. The modern design with iron and aluminum construction ensures durability while maintaining an elegant appearance. Perfect for patios, decks, gardens, and outdoor entertaining areas.',
      category: 'Portable Heater',
      features: ['3000W High Power', 'Telescopic Pole', '3 Heat Settings', 'Remote & Touch Control', 'Tip-over Protection'],
      specs: [
        { label: 'Model', value: 'JHL004-B' },
        { label: 'Product Name', value: 'Freestanding Patio Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '3000W (max)' },
        { label: 'Settings', value: '1200W-1800W-3000W' },
        { label: 'Control', value: 'Remote and Touch button' },
        { label: 'Height', value: 'Telescopic pole (1.6m - 2.08m)' },
        { label: 'Heating Element', value: '2 Halogen Heating tubes' },
        { label: 'Plug', value: 'EU-plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Safety', value: 'Tip-over protection' },
        { label: 'Material', value: 'Iron, Aluminum' }
      ]
    },
    p14: {
      name: 'JHL001-BZ',
      description: 'Efficient halogen patio heater with fixed pole design. Clean and stylish heating solution for outdoor spaces.',
      longDescription:
        'JHL001-BZ is an elegant and efficient patio heater featuring a fixed pole design. Equipped with two halogen heating tubes providing three adjustable power settings (900W-1200W-2100W) for comfortable warmth. The cloth-covered design adds a stylish touch while maintaining functionality. Both remote control and touch button allow easy operation. Features tip-over protection for safety during use. The combination of iron and aluminum materials ensures durability and efficient heat distribution. Ideal for covered patios, gazebos, and other semi-outdoor spaces where style and warmth are equally important.',
      category: 'Portable Heater',
      features: ['3 Heat Settings', 'Cloth Design', 'Remote & Touch Control', 'Tip-over Protection', 'Efficient Halogen Heating'],
      specs: [
        { label: 'Model', value: 'JHL001-BZ' },
        { label: 'Product Name', value: 'Cloth Patio Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '2100W (max)' },
        { label: 'Settings', value: '900W-1200W-2100W' },
        { label: 'Control', value: 'Remote and Touch button' },
        { label: 'Height', value: 'Fixed pole' },
        { label: 'Heating Element', value: '2 Halogen Heating tubes' },
        { label: 'Plug', value: 'EU-plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Safety', value: 'Tip-over protection' },
        { label: 'Material', value: 'Iron, Aluminum' }
      ]
    },
    p15: {
      name: 'JHD028-B',
      description: 'Powerful 2000W ceiling-mounted halogen heater with three heat settings. Perfect for indoor spaces with high ceilings.',
      longDescription:
        'JHD028-B is a ceiling-mounted heater designed for efficient warmth in indoor spaces. Features two halogen heating tubes providing powerful and even heating with three adjustable settings (800W-1200W-2000W). The ceiling installation saves floor space while providing excellent heat distribution from above. Equipped with both remote control and touch button for convenient operation. The aluminum and iron construction ensures durability and efficient heat conduction. Ideal for rooms with high ceilings, workshops, garages, or any space where ceiling mounting is preferred. Provides consistent and comfortable warmth without occupying floor space.',
      category: 'Portable Heater',
      features: ['Ceiling Mounted', '2 Halogen Tubes', '3 Heat Settings', 'Remote & Touch Control', 'Space Saving'],
      specs: [
        { label: 'Model', value: 'JHD028-B' },
        { label: 'Product Name', value: 'Ceiling Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '2000W (max)' },
        { label: 'Settings', value: '800W-1200W-2000W' },
        { label: 'Control', value: 'Remote and Touch button' },
        { label: 'Heating Element', value: '2 Halogen Heating tubes' },
        { label: 'Plug', value: 'EU-plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Material', value: 'Aluminum, Iron' }
      ]
    },
    p16: {
      name: 'JHG019-B',
      description: 'Smart carbon heater with timer function. Wall-mounted design saves space while providing powerful warmth.',
      longDescription:
        'JHG019-B is a smart wall-mounted patio heater featuring advanced carbon fiber heating technology. Provides powerful and efficient warmth with three adjustable settings (650W-1300W-2000W). Features multiple control options including rocker switch, remote control, and touch button for ultimate convenience. The built-in timer function allows automatic shut-off up to 8 hours, providing energy savings and peace of mind. The carbon heating tube ensures rapid heating with excellent heat distribution. The wall-mounted design saves valuable floor space while providing powerful warmth. Made with durable iron and aluminum construction. Perfect for patios, garages, workshops, or any indoor/outdoor space where wall mounting is preferred.',
      category: 'Portable Heater',
      features: ['Wall Mounted', 'Carbon Heating', '8-Hour Timer', 'Multiple Control Options', 'Space Saving Design'],
      specs: [
        { label: 'Model', value: 'JHG019-B' },
        { label: 'Product Name', value: 'Wall-mounted Patio Heater' },
        { label: 'Voltage', value: '220-240V~50Hz' },
        { label: 'Power', value: '2000W (max)' },
        { label: 'Settings', value: '650W-1300W-2000W' },
        { label: 'Switch', value: 'Rocker switch, remote and touch control' },
        { label: 'Remote', value: 'Yes' },
        { label: 'Timer', value: 'Yes, up to 8 hours' },
        { label: 'Heating Element', value: 'Carbon heating tube' },
        { label: 'Plug', value: 'EU-plug' },
        { label: 'Cable', value: '1.8-1.9m' },
        { label: 'Material', value: 'Iron, Aluminum' }
      ]
    },
    p17: {
      name: 'JH-104',
      description: 'Efficient wall-mounted heater with ruby gold halogen tube. Compact design perfect for indoor spaces.',
      longDescription:
        'JH-104 is an efficient wall-mounted heater designed for indoor spaces. Features a ruby gold halogen tube that provides rapid and efficient heating. The compact design with three adjustable heat settings (800W/1300W/2000W) allows you to customize your comfort level. Equipped with remote control for convenient operation. The sleek design fits perfectly in any room while providing powerful warmth. Ideal for living rooms, bedrooms, offices, or any indoor space where wall mounting is preferred.',
      category: 'Portable Heater',
      features: ['Wall Mounted', 'Ruby Gold Halogen Tube', '3 Heat Settings', 'Remote Control', 'Compact Design'],
      specs: [
        { label: 'Model', value: 'JH-104' },
        { label: 'Product Name', value: 'Wall-mounted Heater' },
        { label: 'Voltage', value: '220V ~ 50Hz' },
        { label: 'Power', value: '2000W (max)' },
        { label: 'Settings', value: '800W / 1300W / 2000W' },
        { label: 'Control', value: 'Remote control' },
        { label: 'Heating Element', value: 'Ruby gold halogen tube' },
        { label: 'Plug', value: 'EU plug' },
        { label: 'Cable', value: '3m' },
        { label: 'Dimensions', value: '63.5cm × 10cm' },
        { label: 'Weight', value: '2.2kg' }
      ]
    },
    p18: {
      name: 'JH-106',
      description: 'Compact ceiling-mounted heater with carbon fiber technology. Perfect for small indoor spaces.',
      longDescription:
        'JH-106 is a compact ceiling-mounted heater designed for efficient warmth in small indoor spaces. Features carbon fiber heating tube technology for rapid and efficient heating. The pull-cord switch provides easy on/off control. Three adjustable heat settings (600W/1000W/1500W) allow you to customize your comfort level. The ceiling installation saves floor space while providing excellent heat distribution. Ideal for small rooms, bathrooms, kitchens, or any space where ceiling mounting is preferred.',
      category: 'Portable Heater',
      features: ['Ceiling Mounted', 'Carbon Fiber Technology', '3 Heat Settings', 'Pull-cord Switch', 'Space Saving'],
      specs: [
        { label: 'Model', value: 'JH-106' },
        { label: 'Product Name', value: 'Ceiling Heater' },
        { label: 'Voltage', value: '220V ~ 50Hz' },
        { label: 'Power', value: '1500W (max)' },
        { label: 'Settings', value: '600W / 1000W / 1500W' },
        { label: 'Control', value: 'Pull-cord switch' },
        { label: 'Heating Element', value: 'Carbon fiber heating tube' },
        { label: 'Plug', value: 'EU plug' },
        { label: 'Cable', value: '3m' },
        { label: 'Dimensions', value: '42cm (diameter) × 30cm (height)' },
        { label: 'Weight', value: '2.3kg' }
      ]
    },
    p19: {
      name: 'JH-3000',
      description: 'Premium adjustable patio heater with smartphone app control. Perfect for outdoor entertaining.',
      longDescription:
        'JH-3000 is a premium adjustable patio heater designed for outdoor entertaining. Features advanced carbon fiber heating tube technology for powerful and efficient warmth. The adjustable height (160-205cm) allows you to customize the heating coverage. Equipped with both remote control and smartphone APP for ultimate convenience. Three adjustable heat settings (1200W/1800W/3000W) provide customizable comfort. The sturdy base with 45cm diameter ensures stability. Perfect for patios, decks, gardens, and outdoor entertaining areas.',
      category: 'Portable Heater',
      features: ['Adjustable Height', 'Carbon Fiber Heating', '3 Heat Settings', 'Smartphone APP Control', 'Remote Control'],
      specs: [
        { label: 'Model', value: 'JH-3000' },
        { label: 'Product Name', value: 'Black Umbrella Adjustable Patio Heater' },
        { label: 'Voltage', value: '220V ~ 50Hz' },
        { label: 'Power', value: '3000W (max)' },
        { label: 'Settings', value: '1200W / 1800W / 3000W' },
        { label: 'Control', value: 'Remote control and smartphone APP' },
        { label: 'Heating Element', value: 'Carbon fiber heating tube' },
        { label: 'Plug', value: 'EU plug' },
        { label: 'Cable', value: '5m' },
        { label: 'Height', value: 'Adjustable, 160-205cm' },
        { label: 'Base Diameter', value: '45cm' },
        { label: 'Weight', value: '12kg' }
      ]
    },
  },
};
